import { useIntl } from 'react-intl';
import { ScrollableStack } from '../../../modules/components/scrollableStack';
import {
  Accordion,
  AccordionDetails,
  Button,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  useTheme
} from '@mui/material';
import AccordionSummary from '@mui/material/AccordionSummary';
import { ScoreModelInput } from '../../../modules/layout/inputBlock/scoreModelInput';
import {
  InputSourceButtons,
  InputSourceModes
} from '../../../modules/layout/inputBlock/inputSourceButtons';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import React, { FC, Suspense, useEffect, useRef, useState } from 'react';
import DragDropFile from '../../../modules/xml/xmlDragDrop';
import NewScoreModelButton from '../../../modules/layout/inputBlock/newScoreModelButton';
import { PaperResizer } from '../../../modules/components/paperResizer';
import { ApplicationNumberInput } from '../../../modules/layout/inputBlock/applicationNumberInput';
import useWindowDimensions from '../../../modules/layout/windowDimension';
import { useApplication } from '../../../model/context/applicationContext';
import { useNavigate } from 'react-router-dom';

interface DynamicInputBlockProps {
  LastComponent: FC;
  columnWidth: number;
}

/**
 * Input block with input source and score model and chosen last component
 * @param props Lastcomponent and initial columnwidth
 * @constructor
 */

export const DynamicInputBlock = (props: DynamicInputBlockProps) => {
  const { LastComponent, columnWidth } = props;
  const intl = useIntl();
  const navigate = useNavigate();
  const usePalette = useTheme().palette;
  const lastComponentRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scoreRef = useRef<HTMLInputElement>(null);
  const initialRef = useRef<HTMLInputElement>(null);
  const { height } = useWindowDimensions();

  const {
    applicationNumber: selectedInputApplicationNumber,
    applicationSource: selectedInputSourceMode,
    resetApplication
  } = useApplication();

  const [selectedInputMode, setSelectedInputMode] = useState<InputSourceModes>(
    selectedInputSourceMode || 'Local'
  );
  const [lastComponentHeight, setLastComponentHeight] = useState('');

  const [isInputSourceExpanded, setIsInputSourceExpanded] = useState(true);
  const [isScoreModelExpanded, setIsScoreModelExpanded] = useState(true);

  const handleInputSourceAccordionChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    setIsInputSourceExpanded(isExpanded);
  };

  const handleScoreModelAccordionChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    setIsScoreModelExpanded(isExpanded);
  };

  const handleInputSourceButtonClick = (buttonId: InputSourceModes) => {
    setSelectedInputMode(buttonId);
  };

  const [applicationNumberInputValue, setApplicationNumberInputValue] = useState(
    // Don't set InputValue to value of applicationNumber from the uploaded XML file
    selectedInputSourceMode === 'Dploy' ? selectedInputApplicationNumber : ''
  );

  useEffect(() => {
    if (lastComponentRef.current) {
      const { top } = lastComponentRef.current.getBoundingClientRect();
      const componentHeight = height - top;
      setLastComponentHeight(componentHeight + 'px');
    }
  }, [
    isInputSourceExpanded,
    isScoreModelExpanded,
    inputRef?.current?.getBoundingClientRect(),
    scoreRef?.current?.getBoundingClientRect()
  ]);

  return (
    <PaperResizer
      color={usePalette.background.paper}
      columnId="inputBlock"
      initialColumnWidth={columnWidth}>
      <Stack height="100vh" ref={initialRef} sx={{ overflow: 'hidden' }}>
        <Suspense fallback={<LinearProgress />}>
          <Accordion
            ref={inputRef}
            elevation={0}
            sx={{ borderBottom: 1, borderBottomColor: usePalette.secondary.dark }}
            expanded={isInputSourceExpanded}
            onChange={handleInputSourceAccordionChange}
            defaultExpanded={true}
            disableGutters={true}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header">
              <Typography variant={'h3'}>{intl.formatMessage({ id: 'InputSource' })}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <InputSourceButtons
                buttonClick={(buttonId) => handleInputSourceButtonClick(buttonId)}
                inputSourceMode={selectedInputMode}
              />
              <div>
                <Typography gutterBottom>
                  <span style={{ fontWeight: 700 }}>Active source: </span>
                  <span>{selectedInputSourceMode}</span>
                </Typography>
                <Typography gutterBottom>
                  <span style={{ fontWeight: 700 }}>
                    {intl.formatMessage({ id: 'ApplicationNumber' })}:{' '}
                  </span>
                  <span>{selectedInputApplicationNumber}</span>
                </Typography>
              </div>
              <Grid container sx={{ maxWidth: 283 }}>
                <Grid item sx={{ width: '100%' }}>
                  {selectedInputMode === 'Local' && <DragDropFile />}
                  {selectedInputMode === 'Dploy' && (
                    <ApplicationNumberInput
                      inputValue={applicationNumberInputValue}
                      setInputValue={setApplicationNumberInputValue}
                    />
                  )}
                </Grid>
                <Grid item container sx={{ mt: 1 }} justifyContent={'space-between'}>
                  <Button variant="contained" color="primary" onClick={() => navigate('/xml')}>
                    {intl.formatMessage({ id: 'Xml' })}
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      resetApplication();
                      setApplicationNumberInputValue('');
                    }}>
                    {intl.formatMessage({ id: 'ClearInputSource' })}
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Accordion
            ref={scoreRef}
            elevation={0}
            sx={{ borderBottom: 1, borderBottomColor: usePalette.secondary.dark }}
            expanded={isScoreModelExpanded}
            onChange={handleScoreModelAccordionChange}
            defaultExpanded={true}
            disableGutters={true}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header">
              <Typography variant={'h3'}>{intl.formatMessage({ id: 'ScoreModel' })}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Paper sx={{ borderColor: 'primary' }} elevation={0} variant={'outlined'}>
                <ScrollableStack>
                  <ScoreModelInput />
                </ScrollableStack>
              </Paper>
              <NewScoreModelButton />
            </AccordionDetails>
          </Accordion>
          <div style={{ height: lastComponentHeight }} ref={lastComponentRef}>
            <LastComponent />
          </div>
        </Suspense>
      </Stack>
    </PaperResizer>
  );
};
